import { useEffect, useState } from "react";
import { CalendarRange, Download, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function CampminderPlannedAbsencesPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await api.get("/sync/campminder/planned-absences/status");
      setStatus(r.data);
    } catch (e) {
      // silent — panel is best-effort
    }
  };

  useEffect(() => { loadStatus(); }, []);

  // Poll for live progress while a sync is in flight. The sync now runs in
  // the background (used to block ~10 minutes and trigger 502s); the status
  // endpoint exposes polled/total/failures we can stream into the UI.
  useEffect(() => {
    if (!status?.running) return;
    const t = setInterval(loadStatus, 4000);
    return () => clearInterval(t);
  }, [status?.running]);

  const pullNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sync/campminder/planned-absences");
      if (r.data?.already_running) {
        toast.info("Sync already running — watch progress below.");
      } else if (r.data?.started) {
        toast.success(`Sync started in background for ${r.data.staff_to_poll} staff. Progress will stream below.`);
      } else if (r.data?.stats) {
        // Legacy synchronous response, just in case.
        const s = r.data.stats;
        const total = (s.created || 0) + (s.updated || 0);
        toast.success(total === 0
          ? `No CampMinder absences found across ${r.data?.staff_polled || 0} staff.`
          : `Pulled CampMinder absences: ${s.created || 0} created · ${s.updated || 0} updated · ${s.deleted || 0} removed.`);
      }
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull failed");
    } finally {
      setBusy(false);
    }
  };

  const last = status?.last_run_stats;
  const lastAt = status?.last_run_at_utc;
  const progress = status?.progress || {};
  const running = !!status?.running;
  const pct = progress.total ? Math.min(100, Math.round((progress.polled || 0) * 100 / progress.total)) : 0;

  return (
    <div className="mt-6 pt-6 border-t border-slate-200" data-testid="cm-planned-absences-panel">
      <div className="flex items-center gap-2 mb-1"><CalendarRange className="w-4 h-4 text-amber-600" /><h3 className="font-display text-base font-bold">CampMinder Planned Absences</h3></div>
      <p className="text-sm text-slate-500 mb-3">
        Pull Day1S–Day38S and Mtg1S–Mtg4S Yes/No custom fields from CampMinder.
        Each Yes becomes a planned absence in Staff Cards. Manual edits in this app always win — sync never overwrites them.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={pullNow}
          disabled={busy}
          className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold"
          data-testid="cm-pull-planned-absences"
        >
          {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {busy ? "Pulling…" : running ? "Sync running…" : "Pull Planned Absences from CampMinder"}
        </button>
        <button onClick={loadStatus} className="text-xs text-slate-500 hover:text-slate-700 underline">refresh status</button>
      </div>

      {running && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="cm-pa-progress">
          <div className="flex items-center justify-between text-xs font-semibold text-amber-900 mb-1.5">
            <span>Background sync in progress…</span>
            <span className="font-mono">
              {progress.polled || 0} / {progress.total || 0}
              {(progress.failures || 0) > 0 ? ` · ${progress.failures} failed` : ""}
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-amber-200/80 overflow-hidden">
            <div className="h-full bg-amber-600 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {status?.last_error && !running && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800" data-testid="cm-pa-error">
          <strong>Last sync error:</strong> {status.last_error}
        </div>
      )}

      {status && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-xs" data-testid="cm-pa-status">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
            <div className="text-slate-500 uppercase tracking-[0.14em] font-bold text-[10px]">Active CM absences</div>
            <div className="font-display text-lg font-bold tabular-nums">{status.active_campminder_absences ?? 0}</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
            <div className="text-slate-500 uppercase tracking-[0.14em] font-bold text-[10px]">Suppressed (you deleted)</div>
            <div className="font-display text-lg font-bold tabular-nums">{status.suppressed_count ?? 0}</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
            <div className="text-slate-500 uppercase tracking-[0.14em] font-bold text-[10px]">Last created</div>
            <div className="font-display text-lg font-bold tabular-nums">{last?.created ?? "—"}</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
            <div className="text-slate-500 uppercase tracking-[0.14em] font-bold text-[10px]">Last deleted</div>
            <div className="font-display text-lg font-bold tabular-nums">{last?.deleted ?? "—"}</div>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2" title="Yes flags in CampMinder that we ignored because you already have a manual entry on that date.">
            <div className="text-amber-700 uppercase tracking-[0.14em] font-bold text-[10px]">Skipped (manual)</div>
            <div className="font-display text-lg font-bold tabular-nums text-amber-900">{last?.skipped_admin ?? "—"}</div>
          </div>
        </div>
      )}
      {lastAt && (
        <div className="mt-2 text-[11px] text-slate-500 font-mono">last run: {lastAt.slice(0, 16).replace("T", " ")} UTC</div>
      )}
      {last?.error && (
        <div className="mt-2 text-xs text-red-600 font-mono" data-testid="cm-pa-error">Error: {last.error}</div>
      )}

      <ProbeBlock />
    </div>
  );
}

function ProbeBlock() {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    const q = name.trim();
    if (!q) { toast.error("Type a staff name first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.get("/sync/campminder/planned-absences/probe", { params: { name: q } });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Probe failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-200">
      <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-600 mb-1">Diagnose — probe one staff member</div>
      <div className="text-[11px] text-slate-500 mb-2">If "0 created" looks wrong, type one staff name (e.g. someone you KNOW has Day1S=Yes in CampMinder) to see what the API actually returns.</div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") run(); }}
          placeholder="Staff name (e.g. John Smith)"
          className="flex-1 w-full min-w-0 px-3 py-2 border border-slate-300 rounded-lg text-sm"
          data-testid="cm-probe-input"
        />
        <button
          onClick={run}
          disabled={busy}
          className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold text-sm w-full sm:w-auto"
          data-testid="cm-probe-btn"
        >
          {busy ? "Probing…" : "Probe"}
        </button>
      </div>
      {result && (
        <div className="mt-3 space-y-2 text-xs" data-testid="cm-probe-result">
          <div>Person: <span className="font-mono">{result.matched_crew_name} (PID {result.person_id})</span></div>
          <div>Recognized Day#/Mtg# field defs in CampMinder: <strong>{result.matched_field_defs_count}</strong></div>
          <div>Non-empty values found: <strong>{result.non_empty_value_count}</strong></div>
          <div>Yes-flag count: <strong className={result.yes_flag_count > 0 ? "text-emerald-600" : "text-amber-700"}>{result.yes_flag_count}</strong></div>
          <details>
            <summary className="cursor-pointer text-slate-500 hover:text-slate-800">show raw JSON</summary>
            <pre className="mt-2 p-3 bg-slate-900 text-slate-100 rounded-lg overflow-auto max-h-72 text-[10px]">{JSON.stringify(result, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}
